import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

type Bar = { left: number; right: number }

// Резиновая полоска: ведущий край уходит быстро, ведомый подтягивается с
// задержкой, и полоска на ходу растягивается, как жвачка.
const FAST = '240ms cubic-bezier(0.3, 1.25, 0.4, 1)'
const SLOW = '380ms cubic-bezier(0.3, 1, 0.4, 1) 70ms'

/**
 * Подчёркивание под активной вкладкой с «резиновым» переездом. Общее для вкладок
 * настроек (ссылки) и вкладок статистики (кнопки): один механизм измерения, а не
 * две копии. `itemRef(i)` вешается на каждую вкладку, `navRef` — на их контейнер.
 */
export const useRubberBar = (active: number) => {
  const navRef = useRef<HTMLElement>(null)
  const items = useRef<(HTMLElement | null)[]>([])
  const previous = useRef(active)
  const [bar, setBar] = useState<Bar | null>(null)
  const [movingRight, setMovingRight] = useState(true)

  const measure = useCallback(() => {
    const nav = navRef.current
    const item = items.current[active]
    if (!nav || !item) return
    setBar({ left: item.offsetLeft, right: nav.clientWidth - (item.offsetLeft + item.offsetWidth) })
  }, [active])

  useLayoutEffect(() => {
    setMovingRight(active >= previous.current)
    previous.current = active
    measure()
  }, [active, measure])

  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  const barStyle: CSSProperties | null = bar
    ? {
        left: bar.left,
        right: bar.right,
        transition: movingRight ? `right ${FAST}, left ${SLOW}` : `left ${FAST}, right ${SLOW}`,
      }
    : null

  const itemRef = (index: number) => (el: HTMLElement | null) => {
    items.current[index] = el
  }

  return { navRef, itemRef, barStyle }
}

export const BAR_CLASS =
  'pointer-events-none absolute bottom-0 h-0.5 rounded-full bg-foreground motion-reduce:!transition-none'
